"use client"

import type React from "react"

import { useLocation } from "react-router-dom"
import { useEffect, useState } from "react"
import { AppSidebar } from "@/components/app-sidebar"
import { SidebarProvider } from "@/components/ui/sidebar"
import { AuthProvider, useAuth } from "@/providers/auth-provider"
import { SelectedAppProvider } from "@/providers/selected-app-provider"

// Public paths that don't require authentication
const publicPaths = ["/login", "/register", "/forgot-password", "/reset-password", "/password-reset"]

interface AuthLayoutWrapperProps {
  children: React.ReactNode
}

// This component wraps the children with the AuthProvider
export function AuthLayoutWrapper({ children }: AuthLayoutWrapperProps) {
  return (
    <AuthProvider>
      <AuthenticatedLayout>{children}</AuthenticatedLayout>
    </AuthProvider>
  )
}

// Component that handles the sidebar content
function SidebarLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-screen w-full">
      <AppSidebar />
      <div className="flex-1 w-full min-w-0 overflow-auto">
        {children}
      </div>
    </div>
  )
}

// This component handles the conditional rendering based on auth state
function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()
  const pathname = location.pathname
  const [mounted, setMounted] = useState(false)

  // Prevent hydration mismatch by only rendering after mount
  useEffect(() => {
    setMounted(true)
  }, [])

  // Don't render anything until we've mounted to avoid hydration issues
  if (!mounted) {
    return <div className="h-screen w-full">{children}</div>
  }

  // Check if the current path is a public path
  const isPublicPath = publicPaths.some((path) => pathname === path || pathname.startsWith(`${path}/`))

  // If it's a public path or the user is not authenticated, render without sidebar
  if (isPublicPath || (!isAuthenticated && !isLoading)) {
    return <div className="h-screen w-full">{children}</div>
  }

  // If authenticated, render with sidebar and app context
  return (
    <SelectedAppProvider>
      <SidebarProvider>
        <SidebarLayout>{children}</SidebarLayout>
      </SidebarProvider>
    </SelectedAppProvider>
  )
}
